/**
 * Wat er op één regel van de ritlijst staat.
 *
 * ------------------------------------------------------------------
 * Drie kleine functies in een eigen bestand, en dat is geen netheid maar een
 * reparatie. De eerste versie van het ritscherm haalde de naam uit
 * `useConfig().getAuthority(m.authority)`, en op het scherm stond vijf keer
 * **"Onbekend"** — op een rit waar een trajectcontrole, drie vaste flitsers en
 * een kapot voertuig stonden, allemaal met een naam die de server gewoon weet.
 *
 * De oorzaak, gemeten tegen `GET /api/config/all` op 9 oktober en niet geraden:
 * de catalogus heeft 23 types, verdeeld over drie categorieën — 12
 * `authority`, 7 `infrastructure`, 4 `situation`. En `ConfigProvider` bouwt
 * `authorities` als `all.filter(a => a.category === 'authority')`. Dat is met
 * opzet zo (de Watch-tab filtert op diensten), maar het betekent dat
 * `getAuthority` **elf van de 23 types niet kent** — en net die elf zijn alle
 * flitsers, alle trajectcontroles, alle wegwerkzaamheden en alle situaties.
 * Oftewel: zo ongeveer alles wat op een rit staat.
 *
 * `types` is de volledige lijst en is dus de juiste. Hetzelfde als
 * `Catalog.byId()` op Android, dat ook over alle types gaat.
 *
 * Daarom staat het hier, met tests. Een verkeerde catalogus geeft geen
 * foutmelding en geen lege plek; hij geeft een geloofwaardig verkeerd woord.
 *
 * Alle drie gespiegeld op `RitScreen.kt` regel 198-214: wie in de mini-app een
 * andere naam of een ander getal krijgt dan in de app, gebruikt twee producten.
 */

/**
 * De naam van het soort melding, uit de catalogus.
 *
 * Valt terug op het rúwe id en niet op "Onbekend", net als
 * `Catalog.byId(m.authority)?.name ?: m.authority` op Android. "Trajectcontrole"
 * missen en "trajectcontrole" tonen is een schoonheidsfoutje; "Onbekend" tonen
 * is een uitspraak over de melding die niet waar is — het suggereert dat wíj
 * niet weten wat voor controle het is, terwijl we het wel weten en alleen de
 * verkeerde lijst raadpleegden.
 */
export const ritTitel = (types, melding) => {
  const id = (melding && melding.authority) || "";
  const gevonden = Array.isArray(types) ? types.find(t => t && t.id === id) : null;
  return (gevonden && gevonden.name) || id || "Melding";
};

/**
 * De regel onder de naam: waar het staat en wat er geldt.
 *
 * Krijgt de uitkomst van `roadLine()` mee in plaats van die zelf te roepen,
 * zodat dit zuiver blijft. `road` is leeg wanneer de straatnaam alleen het
 * wegnummer wás — dan zegt het schild het al en valt deze regel terug op wat
 * erachter stond.
 */
export const ritOnder = (melding, weg) => {
  const m = melding || {};
  const w = weg || {};
  return [w.road || w.rest, m.maxspeed ? `max ${m.maxspeed}` : null]
    .filter(Boolean).join(" · ");
};

/**
 * De kilometer waarop je hem bereikt, in het Nederlands.
 *
 * Altijd één decimaal, ook onder de kilometer: "0,4" en niet "400 m". Dat is
 * een keuze tegen `feedDistance` in, die wél omschakelt — maar dit is een
 * kolom getallen die onder elkaar moet uitlijnen, en het is dezelfde tekst als
 * RitScreen.kt drukt.
 *
 * `route_m` is meters en komt als float met één decimaal uit route_match.
 *
 * Ontbreekt het, dan komt er niets en geen "0,0". `Number(null)` is nul, dus
 * zonder die eerste regel leest een ontbrekende kilometer op het scherm als
 * "je bent er nu" — een onwaarheid in plaats van een lege plek.
 */
export const ritKm = (routeM) => {
  if (routeM === null || routeM === undefined || routeM === "") return "";
  const m = Number(routeM);
  if (!Number.isFinite(m)) return "";
  return (m / 1000).toLocaleString("nl-NL", {
    minimumFractionDigits: 1, maximumFractionDigits: 1,
  });
};
